import React, { createContext, useState, useEffect, useContext } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import api from "./api";
import i18n from "./i18n";
import { isShareHost } from "./utils/appHost";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [user, setUser] = useState(null);
  const [events, setEvents] = useState([]);
  const [joinedEvents, setJoinedEvents] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();

  const checkAuth = async () => {
    try {

      // if page is / return (on share.pic-loud.com, / is the signed-in home)
      if (location.pathname === "/" && !isShareHost) return;

      // 1. Get auth + user
      const res = await api.get(`/auth/me`, { withCredentials: true });
      setAuthenticated(res.data.authenticated);

      const currentUser = res.data.user; // store in local variable
      setUser(currentUser);

      // Apply user's preferred language for admin routes
      if (currentUser?.language) {
        i18n.changeLanguage(currentUser.language);
      }

      // 2. If user exists, fetch owned events and joined events
      let eventsRes = { data: [] };
      let joinedRes = { data: { events: [] } };
      if (currentUser && !isShareHost) {
        [eventsRes, joinedRes] = await Promise.all([
          api.get(`/events?user_id=${currentUser.user_id}`, { withCredentials: true }),
          api.get(`/events/joined`, { withCredentials: true }).catch(() => ({ data: { events: [] } })),
        ]);
      }
      setEvents(eventsRes.data);
      setJoinedEvents(joinedRes.data?.events ?? []);
    } catch (error) {
      console.error("Auth check failed:", error);
      setAuthenticated(false);
      setUser(null);
      setEvents([]);
      setJoinedEvents([]);
    } finally {
      setLoadingAuth(false);
    }
  };

  const refreshUser = async () => {
    try {
      const res = await api.get(`/auth/me`, { withCredentials: true });
      setUser(res.data.user);
    } catch (error) {
      console.error("Error refreshing user:", error);
    }
  };

    const refreshEvents = async () => {
    if (!user) return;
    try {
      const res = await api.get(`/events?user_id=${user.user_id}`, {
        withCredentials: true,
      });
      setEvents(res.data);
    } catch (error) {
      console.error("Error refreshing events:", error);
    }
  };

  
  const refreshJoinedEvents = async () => {
    try {
      const res = await api.get(`/followers/joined`, { withCredentials: true });
      setJoinedEvents(res.data?.events ?? []);
    } catch (error) {
      console.error("Error refreshing joined events:", error);
    }
  };

  const logout = async () => {
    await api.post(`/auth/logout`, {}, { withCredentials: true });
    setAuthenticated(false);
    setUser(null);
  };

  useEffect(() => {
    checkAuth();
  }, []);

  // After Google OAuth, the backend lands on /admin/events — redirect back to
  // the page the user was on before they clicked "Continue with Google".
  useEffect(() => {
    if (loadingAuth || !authenticated) return;
    const redirect = sessionStorage.getItem("auth_redirect");
    if (redirect) {
      sessionStorage.removeItem("auth_redirect");
      navigate(redirect, { replace: true });
    }
  }, [loadingAuth, authenticated]);

  // define protected paths
  const protectedRoutes = [
    "/admin",
    "/admin/events",
    "/admin/upload",
    "/admin/:id_name",
    "/admin/settings",
    "/social-events",
    "/support",
    "/admin/support",
  ];

  // redirect if not authenticated and trying to access protected route
  if (
    !loadingAuth &&
    !authenticated &&
    protectedRoutes.some((r) =>
      location.pathname.startsWith(r.replace(":id_name", ""))
    )
  ) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        refreshUser,
        events,
        setEvents,
        joinedEvents,
        setJoinedEvents,
        refreshJoinedEvents,
        authenticated,
        loadingAuth,
        checkAuth,
        logout,
        refreshEvents,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
